import { useEffect, useState } from 'react';
import { useWizard } from '../wizardContext';
import { fmtBRL, fmtNum, maskThousand, onlyDigits } from '../utils/finance';
import {
  fetchBrands,
  fetchModels,
  fetchReferenceCode,
  fetchValue,
  fetchYears,
  type FipeOption,
} from '../utils/fipe';
import { AlertIcon } from '../parts/icons';
import { RichSelect, type RichSelectOption } from '../parts/RichSelect';

const TIPO_CARRO = '1' as const;

export const Step3Seminovo = () => {
  const { state, set, next, prev } = useWizard();
  const t = state.tradeIn;
  const errors = state.errors;

  // Dados da FIPE (locais à etapa — cascata marca → modelo → ano → valor).
  const [ref, setRef] = useState('');
  const [brands, setBrands] = useState<FipeOption[]>([]);
  const [models, setModels] = useState<FipeOption[]>([]);
  const [years, setYears] = useState<FipeOption[]>([]);
  const [loading, setLoading] = useState<'brands' | 'models' | 'years' | 'value' | null>(null);
  const [fipeError, setFipeError] = useState(false);

  const fipeValue = t.fipeValue;
  const max = fipeValue;
  const fipeReady = !!(t.brandCode && t.modelCode && t.yearCode && fipeValue > 0);

  const clearErr = (key: string) => set((d) => { if (d.errors[key]) delete d.errors[key]; });

  // Ao abrir: tabela de referência do mês + marcas (carro).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading('brands');
        setFipeError(false);
        const refCode = await fetchReferenceCode();
        const brandList = await fetchBrands(refCode, TIPO_CARRO);
        if (!alive) return;
        setRef(refCode);
        setBrands(brandList);
      } catch {
        if (alive) setFipeError(true);
      } finally {
        if (alive) setLoading(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  const onSelectBrand = async (code: string) => {
    const label = brands.find((b) => b.value === code)?.label ?? '';
    set((d) => {
      d.tradeIn.brand = label;
      d.tradeIn.brandCode = code;
      d.tradeIn.model = '';
      d.tradeIn.modelCode = '';
      d.tradeIn.year = '';
      d.tradeIn.yearCode = '';
      d.tradeIn.fipeValue = 0;
      d.tradeIn.askingValue = 0;
    });
    setModels([]);
    setYears([]);
    clearErr('tiBrand');
    try {
      setLoading('models');
      setFipeError(false);
      const list = await fetchModels(ref, TIPO_CARRO, code);
      setModels(list);
    } catch {
      setFipeError(true);
    } finally {
      setLoading(null);
    }
  };

  const onSelectModel = async (code: string) => {
    const label = models.find((m) => m.value === code)?.label ?? '';
    set((d) => {
      d.tradeIn.model = label;
      d.tradeIn.modelCode = code;
      d.tradeIn.year = '';
      d.tradeIn.yearCode = '';
      d.tradeIn.fipeValue = 0;
      d.tradeIn.askingValue = 0;
    });
    setYears([]);
    clearErr('tiModel');
    try {
      setLoading('years');
      setFipeError(false);
      const list = await fetchYears(ref, TIPO_CARRO, t.brandCode, code);
      setYears(list);
    } catch {
      setFipeError(true);
    } finally {
      setLoading(null);
    }
  };

  const onSelectYear = async (value: string) => {
    const ano = value.split('-')[0];
    set((d) => {
      d.tradeIn.year = ano;
      d.tradeIn.yearCode = value;
      d.tradeIn.fipeValue = 0;
      d.tradeIn.askingValue = 0;
    });
    clearErr('tiYear');
    try {
      setLoading('value');
      setFipeError(false);
      const valor = await fetchValue(ref, TIPO_CARRO, t.brandCode, t.modelCode, value);
      set((d) => { d.tradeIn.fipeValue = valor; });
    } catch {
      setFipeError(true);
    } finally {
      setLoading(null);
    }
  };

  const onContinue = () => {
    const errs: Record<string, string> = {};
    if (!t.brandCode) errs.tiBrand = 'Selecione a marca.';
    if (!t.modelCode) errs.tiModel = 'Selecione o modelo.';
    if (!t.yearCode) errs.tiYear = 'Selecione o ano.';
    if (!(t.km > 0 && t.km <= 500000)) errs.tiKm = 'Informe uma quilometragem entre 1 e 500.000 km.';
    if (!(t.askingValue > 0)) errs.tiAsk = 'Informe por quanto deseja vender o seminovo.';
    if (Object.keys(errs).length) {
      set((d) => { d.errors = errs; });
      return;
    }
    next();
  };

  const selectField = (
    errKey: string,
    label: string,
    value: string,
    options: RichSelectOption[],
    onChange: (v: string) => void,
    opts: { disabled?: boolean; monogram?: boolean; isLoading?: boolean } = {},
  ) => (
    <div className={`lpfs-field${errors[errKey] ? ' is-error' : ''}`}>
      <label className="lpfs-label">{label}</label>
      <RichSelect
        ariaLabel={label}
        disabled={opts.disabled || opts.isLoading}
        monogram={opts.monogram}
        placeholder={opts.isLoading ? 'Carregando…' : 'Selecione'}
        value={value}
        options={options}
        onChange={onChange}
      />
      {errors[errKey] && (
        <div className="lpfs-error-msg">
          <AlertIcon />
          <span>{errors[errKey]}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="lpfs-anim-in">
      <div className="lpfs-step-head">
        <h2 className="lpfs-step-title">Avaliação do seu seminovo</h2>
        <p className="lpfs-step-sub">Estime o valor do veículo usado que entra na negociação.</p>
      </div>

      {fipeError && (
        <div className="lpfs-fipe-card" style={{ borderColor: 'var(--lpfs-error)' }}>
          <p className="lpfs-fipe-note" style={{ color: 'var(--lpfs-error)' }}>
            Não foi possível consultar a tabela FIPE agora. Tente novamente em instantes.
          </p>
        </div>
      )}

      <div className="lpfs-form-grid">
        {selectField(
          'tiBrand',
          'Marca',
          t.brandCode,
          brands.map((b) => ({ value: b.value, label: b.label, imageKind: 'logo' as const })),
          onSelectBrand,
          { monogram: true, isLoading: loading === 'brands' },
        )}
        {selectField(
          'tiModel',
          'Modelo',
          t.modelCode,
          models.map((m) => ({ value: m.value, label: m.label })),
          onSelectModel,
          { disabled: !t.brandCode, isLoading: loading === 'models' },
        )}
        {selectField(
          'tiYear',
          'Ano do modelo',
          t.yearCode,
          years.map((y) => ({ value: y.value, label: y.label })),
          onSelectYear,
          { disabled: !t.modelCode, isLoading: loading === 'years' },
        )}
        <div className={`lpfs-field${errors.tiKm ? ' is-error' : ''}`}>
          <label className="lpfs-label">Quilometragem</label>
          <input
            className="lpfs-input"
            inputMode="numeric"
            value={t.km ? fmtNum(t.km) : ''}
            onChange={(e) => {
              const km = parseInt(onlyDigits(maskThousand(e.target.value)) || '0', 10);
              set((d) => { d.tradeIn.km = km; });
              clearErr('tiKm');
            }}
          />
          {errors.tiKm && (
            <div className="lpfs-error-msg">
              <AlertIcon />
              <span>{errors.tiKm}</span>
            </div>
          )}
        </div>
      </div>

      {loading === 'value' && (
        <p className="lpfs-fipe-note" style={{ marginTop: 12 }}>Consultando o preço médio…</p>
      )}

      {fipeReady && (
        <div className="lpfs-fipe-card">
          <p className="lpfs-fipe-label">Preço médio estimado</p>
          <p className="lpfs-fipe-value">{fmtBRL(fipeValue)}</p>
          <p className="lpfs-fipe-note">* Preço médio extraído da tabela FIPE.</p>
        </div>
      )}

      {fipeReady && (
        <div className="lpfs-fieldset">
          <p className="lpfs-section-label">Por quanto deseja vender?</p>
          <div className="lpfs-slider-row">
            <div className="lpfs-money-input">
              <span>R$</span>
              <input
                inputMode="numeric"
                value={fmtNum(t.askingValue || 0)}
                onChange={(e) => {
                  let val = parseInt(onlyDigits(e.target.value) || '0', 10);
                  val = Math.max(0, Math.min(max, val));
                  set((d) => { d.tradeIn.askingValue = val; });
                  clearErr('tiAsk');
                }}
              />
            </div>
          </div>
          <div className="lpfs-slider-wrap">
            <input
              type="range"
              className="lpfs-range"
              min={0}
              max={max}
              step={1}
              value={t.askingValue || 0}
              style={{ ['--lpfs-fill' as string]: `${max > 0 ? ((t.askingValue || 0) / max) * 100 : 0}%` }}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                set((d) => { d.tradeIn.askingValue = val; });
                clearErr('tiAsk');
              }}
            />
          </div>
          <div className="lpfs-slider-scale">
            <span>{fmtBRL(0)}</span>
            <span>{fmtBRL(max)}</span>
          </div>
          <p className="lpfs-fipe-note" style={{ marginTop: 10 }}>
            Esse valor é apenas uma referência. A avaliação final é feita presencialmente na concessionária.
          </p>
        </div>
      )}

      <div className="lpfs-actions">
        <button type="button" className="lpfs-btn lpfs-btn-ghost" onClick={prev}>
          Voltar
        </button>
        <span className="lpfs-spacer" />
        <button type="button" className="lpfs-btn lpfs-btn-primary" onClick={onContinue}>
          Continuar
        </button>
      </div>
    </div>
  );
};
